import { InitialPagination } from '../features/pagination/types/initialPagination.type';
import { State } from '../state/types/state';
import { SnapshotSets } from './types/snapshot-sets';
import { URLStorage } from './URLStorage';

/**
 * Responsible for storing and retrieving the state of the SERP and corresponding searchQuery.
 * The state (controls) is pushed to the permanent storage (URL).
 * Search query is stored in URL.
 */
export class Persistence {
    private urlStorage: URLStorage;

    constructor() {
        this.urlStorage = URLStorage.getInstance();
    }

    /**
     * Updates storages with the current state of the SERP & searchQuery.
     * called after successful search, when results are rewritten.
     */
    public updateStorage({
        searchQuery,
        controlsState,
    }: {
        searchQuery: string;
        controlsState: State['controls'];
    }): void {
        this.urlStorage.setSearchQuery(searchQuery);
        this.urlStorage.setControlsState(controlsState);
    }

    /**
     * Updates url storage when transitioning from instant to full SERP
     */
    public updateURLQuery(searchQuery: string): void {
        this.urlStorage.setSearchQuery(searchQuery);
    }

    /**
     * Updates storages with the state of pagination after 'load more' action.
     */
    public updateStoragePagination({ querySetIdentifier, size }: { querySetIdentifier: string; size: number }): void {
        this.urlStorage.updatePagination(querySetIdentifier, size);
    }

    /**
     * Clears the history stack and search query from URL.
     * Called after reseting and/or closing the SERP.
     */
    public clearStorage(): void {
        this.urlStorage.clearSearchParams();
    }

    /**
     * Retrieves the state of the SERP and searchQuery from URL.
     * Called when initializing the SERP app.
     */
    public getPersistentState(): {
        searchQuery: string;
        snapshotSets: SnapshotSets | null;
        pagination: InitialPagination | null;
    } {
        const searchQuery = this.getSearchQueryFromURL();
        const snapshotSets = this.getSnapshotSetsFromURL();
        const pagination = this.urlStorage.getPagination();

        return { searchQuery, snapshotSets, pagination };
    }

    /**
     * Retrieves the search query from URL.
     */
    public getSearchQueryFromURL(): string {
        const query = this.urlStorage.getSearchQuery();
        if (query !== null) {
            return query;
        }
        return '';
    }

    /**
     * Retrieves the state of the SERP controls from URL.
     */
    public getSnapshotSetsFromURL(): SnapshotSets | null {
        return this.urlStorage.getControlsState();
    }

    public getInitialPaginationFromURL(): InitialPagination | null {
        return this.urlStorage.getPagination();
    }
}
